
import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import "../Style/UserManagement.css";

// Helper function to create Axios instance with token
const createAPI = () => {
  const token = localStorage.getItem("token");
  if (!token) {
    console.warn("Authentication token not found.");
  }
  return axios.create({
    baseURL: "https://api.yellowmatrix.co.in",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

// Success notification component
const SuccessNotification = React.memo(({ message, onClose }) => (
  <div className="success-notification">
    <span>{message}</span>
    <button onClick={onClose} className="close-notification">
      ×
    </button>
  </div>
));

const UserManagement = () => {
  // --- ALL REACT HOOKS (useState, useRef, useQuery, useMutation) MUST BE DECLARED AT THE TOP LEVEL ---
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [activeTab, setActiveTab] = useState("organization");
  const [newUser, setNewUser] = useState({
    UserID: null,
    Username: "",
    Email: "",
    Password: "",
    Role: "SuperAdmin",
    CustomerID: "",
    Status: true,
  });
  const [newClientUser, setNewClientUser] = useState({
    UserID: null,
    Username: "",
    Email: "",
    Password: "",
    Role: "User",
    CustomerID: "",
    Status: true,
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    userId: null,
    userName: "",
    userType: "",
  });

  const successTimeoutRef = useRef(null);

  // TanStack Query Hooks for data fetching
  const {
    data: organizationUsers = [],
    isLoading: isLoadingOrgUsers,
    isError: isErrorOrgUsers,
    error: orgUsersFetchError,
  } = useQuery({
    queryKey: ["organizationUsers"],
    queryFn: async () => {
      const API = createAPI();
      const response = await API.get("/org-users");
      return response.data?.["Organization Users"] || [];
    },
    enabled: activeTab === "organization",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const {
    data: clientUsers = [],
    isLoading: isLoadingClientUsers,
    isError: isErrorClientUsers,
    error: clientUsersFetchError,
  } = useQuery({
    queryKey: ["clientUsers", selectedCustomerId],
    queryFn: async () => {
      const API = createAPI();
      const url =
        selectedCustomerId === "All Customers"
          ? "/users"
          : `/users?customer_id=${selectedCustomerId}`;
      const response = await API.get(url);
      return response.data?.Users || [];
    },
    enabled: activeTab === "client" && !!selectedCustomerId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: Customers = [], isLoading: isLoadingCustomers } = useQuery({
    queryKey: ["Customers"],
    queryFn: async () => {
      const API = createAPI();
      const response = await API.get("/customers");
      return response.data || [];
    },
    enabled: activeTab === "client",
    staleTime: 5 * 60 * 1000,
  });

  const customerMap = useMemo(() => {
    if (!Customers || Customers.length === 0) return new Map();
    return new Map(Customers.map((v) => [v.CustomerID, v.CustomerName]));
  }, [Customers]);

 
  // TanStack Query Hooks for mutations
  const addUpdateOrgUserMutation = useMutation({
 mutationFn: async (userData) => {
    const API = createAPI();
    if (editMode) {
      // Create the payload object here
      const payload = {
        user_id: userData.UserID,
        Username: userData.Username,
        Role: userData.Role,
        Status: userData.Status,
        Organization: userData.CustomerID,
      };

      // Only add the password if it's not empty
      if (userData.Password) {
        payload.Password = userData.Password;
      }

      // Send the new payload in the PATCH request
      return await API.patch("/org-users", payload);
      } else {
        return await API.post("/org-users", {
          Email: userData.Email,
          Username: userData.Username,
          Password: userData.Password,
          Role: "SuperAdmin",
          Organization: userData.CustomerID,
          Status: userData.Status,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationUsers"] });
      showSuccess(
        editMode ? "User updated successfully" : "User added successfully"
      );
      resetForm();
      setMutationError(null);
    },
    onError: (err) => {
      console.error("Error saving organization user:", err);
      setMutationError(
        err.response?.data?.message || "Failed to save organization user."
      );
    },
  });

  const addUpdateClientUserMutation = useMutation({
    mutationFn: async (userData) => {
      const API = createAPI();
      if (editMode) {
        return await API.patch("/users", {
          UserID: userData.UserID,
          Username: userData.Username,
          Role: userData.Role,
          Status: userData.Status,
          ...(userData.Password && { Password: userData.Password }),
        });
      } else {
        return await API.post("/users", {
          Email: userData.Email,
          Username: userData.Username,
          Password: userData.Password,
          Role: userData.Role,
          CustomerID: userData.CustomerID,
          Status: userData.Status,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clientUsers", selectedCustomerId],
      });
      showSuccess(
        editMode
          ? "Client user updated successfully"
          : "Client user added successfully"
      );
      resetClientForm();
      setMutationError(null);
    },
    onError: (err) => {
      console.error("Error saving client user:", err);
      setMutationError(
        err.response?.data?.message || "Failed to save client user."
      );
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async ({ userId, userType }) => {
      const API = createAPI();
      if (userType === "organization") {
        return await API.delete("/org-users", { data: { user_id: userId } });
      } else {
        return await API.delete("/users", { data: { UserID: userId } });
      }
    },
    onSuccess: (_, variables) => {
      if (variables.userType === "organization") {
        queryClient.invalidateQueries({ queryKey: ["organizationUsers"] });
        showSuccess("Organization user deleted successfully");
      } else {
        queryClient.invalidateQueries({
          queryKey: ["clientUsers", selectedCustomerId],
        });
        showSuccess("Client user deleted successfully");
      }
      setDeleteConfirmation({
        isOpen: false,
        userId: null,
        userName: "",
        userType: "",
      });
      setMutationError(null);
    },
    onError: (err) => {
      console.error("Error deleting user:", err);
      setMutationError(err.response?.data?.message || "Failed to delete user.");
      setDeleteConfirmation({
        isOpen: false,
        userId: null,
        userName: "",
        userType: "",
      });
    },
  });

  const fetchError =
    activeTab === "organization" ? orgUsersFetchError : clientUsersFetchError;
  const isFetchError =
    activeTab === "organization" ? isErrorOrgUsers : isErrorClientUsers;

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setSelectedCustomerId("");
    setSearchTerm("");
    setRoleFilter("All Roles");
    setStatusFilter("All Status");
  }, [activeTab]);

  const showSuccess = (message) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setSuccessMessage(message);
    successTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
      successTimeoutRef.current = null;
    }, 3000);
  };

  const resetForm = () => {
    setModalOpen(false);
    setEditMode(false);
    setNewUser({
      UserID: null,
      Username: "",
      Email: "",
      Password: "",
      Role: "SuperAdmin",
      CustomerID: "",
      Status: true,
    });
    setShowPassword(false);
    setMutationError(null);
  };

  const resetClientForm = () => {
    setClientModalOpen(false);
    setEditMode(false);
    setNewClientUser({
      UserID: null,
      Username: "",
      Email: "",
      Password: "",
      Role: "User",
      CustomerID: "",
      Status: true,
    });
    setShowPassword(false);
    setMutationError(null);
  };

  const handleEditClick = (user) => {
    setMutationError(null);
    if (activeTab === "organization") {
      setNewUser({ ...user, UserID: user.UserID, Password: "" });
      setModalOpen(true);
    } else {
      setNewClientUser({ ...user, UserID: user.UserID, Password: "" });
      setClientModalOpen(true);
    }
    setEditMode(true);
  };

  const handleDeleteClick = (userId, userName, userType) => {
    setMutationError(null);
    setDeleteConfirmation({
      isOpen: true,
      userId,
      userName,
      userType,
    });
  };

  const handleConfirmDelete = () => {
    if (deleteUserMutation.isPending) return;
    deleteUserMutation.mutate({
      userId: deleteConfirmation.userId,
      userType: deleteConfirmation.userType,
    });
  };

  const handleAddOrUpdateOrgUser = () => {
    if (addUpdateOrgUserMutation.isPending) return;
    addUpdateOrgUserMutation.mutate(newUser);
  };

  const handleAddOrUpdateClientUser = () => {
    if (addUpdateClientUserMutation.isPending) return;
    addUpdateClientUserMutation.mutate(newClientUser);
  };

  const currentUsers =
    activeTab === "organization" ? organizationUsers : clientUsers;
  const filteredUsers = currentUsers.filter((user) => {
    if (!user) return false;
    const matchesSearch =
      (user.Username?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (user.Email?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "All Roles" || user.Role === roleFilter;
    const matchesStatus =
      statusFilter === "All Status" ||
      (statusFilter === "Active" && user.Status) ||
      (statusFilter === "Inactive" && !user.Status);
    const matchesCustomer =
      activeTab === "organization" ||
      selectedCustomerId === "All Customers" ||
      user.CustomerID === selectedCustomerId;
    return matchesSearch && matchesRole && matchesStatus && matchesCustomer;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("en-GB") +
      ", " +
      date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const isTableLoading =
    (activeTab === "organization" && isLoadingOrgUsers) ||
    (activeTab === "client" && isLoadingClientUsers) ||
    (activeTab === "client" && isLoadingCustomers);

  // --- Main Content ---
  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h1 className="user-management-title">User Management</h1>
        <hr className="line" width="100%" />
        <p className="user-management-subtitle">
          Manage organization and client users
        </p>
      </div>

      <div className="user-tabs">
        <button
          className={`tab-btn ${activeTab === "organization" ? "active" : ""}`}
          onClick={() => setActiveTab("organization")}
        >
          Organization Users
        </button>
        <button
          className={`tab-btn ${activeTab === "client" ? "active" : ""}`}
          onClick={() => setActiveTab("client")}
        >
          Client Users
        </button>
      </div>

      <div className="filter-controls">
        <div className="search-container-user">
          <i className="fa-solid fa-magnifying-glass search-icon-user"></i>
          <input
            type="text"
            placeholder={`Search users...`}
            className="search-input-user"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="All Roles">All Roles</option>
          {activeTab === "organization" ? (
            <option value="SuperAdmin">SuperAdmin</option>
          ) : (
            <>
              <option value="Admin">Admin</option>
              <option value="User">User</option>
            </>
          )}
        </select>

        {activeTab === "client" &&
          (isLoadingOrgUsers ? (
            <div
              className="filter-select skeleton-input"
              aria-hidden="true"
            ></div>
          ) : (
            <select
              className="filter-select"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              <option value="" disabled>
                Select a customer
              </option>
              {Customers.map((customer) => (
                <option key={customer.CustomerID} value={customer.CustomerID}>
                  {customer.CustomerName}
                </option>
              ))}
            </select>
          ))}

        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All Status">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        <button
          className="add-user-btn"
          onClick={() => {
            setEditMode(false);
            activeTab === "organization" ? resetForm() : resetClientForm();
            activeTab === "organization"
              ? setModalOpen(true)
              : setClientModalOpen(true);
            setMutationError(null);
          }}
        >
          Add New {activeTab === "organization" ? "User" : "Client User"}
        </button>
      </div>

      {isFetchError && (
        <div className="error-message-user">
          Error fetching users: {fetchError.message}
        </div>
      )}
      {successMessage && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            if (successTimeoutRef.current) {
              clearTimeout(successTimeoutRef.current);
            }
            setSuccessMessage(null);
          }}
        />
      )}
      {mutationError && (
        <div className="error-message-user">{mutationError}</div>
      )}

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>
                {activeTab === "organization" ? "Organization" : "Customer"}
              </th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* --- CHANGED: Table Body Logic --- */}
            {isTableLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(7)].map((_, j) => (
                    <td key={j}>
                      <div className="skeleton-line" aria-hidden="true"></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : activeTab === "client" && !selectedCustomerId ? (
              <tr>
                <td colSpan="7" className="no-data-message">
                  Please select a customer to view users.
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <tr key={user.UserID}>
                  <td data-label="Name">{user.Username}</td>
                  <td data-label="Email">{user.Email}</td>
                  <td data-label="Role">{user.Role}</td>
                  <td
                    data-label={
                      activeTab === "organization" ? "Organization" : "Customer"
                    }
                  >
                    {activeTab === "organization"
                      ? user.CustomerID || "—"
                      : customerMap.get(user.CustomerID) ||
                        user.CustomerID ||
                        "—"}
                  </td>
                  <td data-label="Status">
                    <span
                      className={`status-badge ${
                        user.Status ? "status-active" : "status-inactive"
                      }`}
                    >
                      {user.Status ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td data-label="Last Login">{formatDate(user.LastLogin)}</td>
                  <td data-label="Actions">
                    <button
                      className="action-btn edit-btn"
                      onClick={() => handleEditClick(user)}
                    >
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() =>
                        handleDeleteClick(user.UserID, user.Username, activeTab)
                      }
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data-message">
                  No users found matching your criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- ALL MODALS (UNCHANGED) --- */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {editMode ? "Edit User" : "Add New User"}
              </h3>
            </div>
            {addUpdateOrgUserMutation.isError && (
              <div className="error-message-modal">
                {addUpdateOrgUserMutation.error.response?.data?.message ||
                  addUpdateOrgUserMutation.error.message ||
                  "An error occurred."}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                placeholder="Name"
                className="form-input"
                value={newUser.Username}
                onChange={(e) =>
                  setNewUser({ ...newUser, Username: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                placeholder="Email"
                className="form-input"
                type="email"
                value={newUser.Email}
                onChange={(e) =>
                  setNewUser({ ...newUser, Email: e.target.value })
                }
                required
                disabled={editMode}
              />
            </div>
            {!editMode && (
              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Create password"
                    className="form-input"
                    value={newUser.Password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, Password: e.target.value })
                    }
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="password-hints">
                  Password must contain at least 8 characters, including
                  uppercase, lowercase, number, and special character.
                </div>
              </div>
            )}
            {editMode && (
              <div className="form-group">
                <label className="form-label">
                  New Password (leave blank to keep current)
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password"
                    className="form-input"
                    value={newUser.Password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, Password: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Role</label>
              <input
                type="text"
                className="form-input"
                value="SuperAdmin"
                readOnly
              />
              <small className="form-hint">
                Organization users can only be SuperAdmins
              </small>
            </div>
            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={newUser.Status}
                  onChange={(e) =>
                    setNewUser({ ...newUser, Status: e.target.checked })
                  }
                />
                Active
              </label>
            </div>
            <div className="modal-footer">
              <button onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleAddOrUpdateOrgUser}
                className="submit-btn"
                disabled={addUpdateOrgUserMutation.isPending}
              >
                {addUpdateOrgUserMutation.isPending
                  ? "Saving..."
                  : editMode
                  ? "Update User"
                  : "Add User"}
              </button>
            </div>
          </div>
        </div>
      )}
      {clientModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {editMode ? "Edit Client User" : "Add New Client User"}
              </h3>
            </div>
            {addUpdateClientUserMutation.isError && (
              <div className="error-message-modal">
                {addUpdateClientUserMutation.error.response?.data?.message ||
                  addUpdateClientUserMutation.error.message ||
                  "An error occurred."}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                placeholder="Name"
                className="form-input"
                value={newClientUser.Username}
                onChange={(e) =>
                  setNewClientUser({
                    ...newClientUser,
                    Username: e.target.value,
                  })
                }
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                placeholder="Email"
                className="form-input"
                type="email"
                value={newClientUser.Email}
                onChange={(e) =>
                  setNewClientUser({ ...newClientUser, Email: e.target.value })
                }
                required
                disabled={editMode}
              />
            </div>
            {!editMode && (
              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Create password"
                    className="form-input"
                    value={newClientUser.Password}
                    onChange={(e) =>
                      setNewClientUser({
                        ...newClientUser,
                        Password: e.target.value,
                      })
                    }
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="password-hints">
                  Password must contain at least 8 characters, including
                  uppercase, lowercase, number, and special character.
                </div>
              </div>
            )}
            {editMode && (
              <div className="form-group">
                <label className="form-label">
                  New Password (leave blank to keep current)
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password"
                    className="form-input"
                    value={newClientUser.Password}
                    onChange={(e) =>
                      setNewClientUser({
                        ...newClientUser,
                        Password: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-select"
                value={newClientUser.Role}
                onChange={(e) =>
                  setNewClientUser({ ...newClientUser, Role: e.target.value })
                }
                required
              >
                <option value="User">User</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            {!editMode && (
              <div className="form-group">
                <label className="form-label">Customer Name</label>
                <select
                  className="form-select"
                  value={newClientUser.CustomerID}
                  onChange={(e) =>
                    setNewClientUser({
                      ...newClientUser,
                      CustomerID: e.target.value,
                    })
                  }
                  required
                >
                  <option value="" disabled>
                    Select a customer
                  </option>
                  {Customers.map((customer) => (
                    <option
                      key={customer.CustomerID}
                      value={customer.CustomerID}
                    >
                      {customer.CustomerName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={newClientUser.Status}
                  onChange={(e) =>
                    setNewClientUser({
                      ...newClientUser,
                      Status: e.target.checked,
                    })
                  }
                />
                Active
              </label>
            </div>
            <div className="modal-footer">
              <button onClick={resetClientForm} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleAddOrUpdateClientUser}
                className="submit-btn"
                disabled={addUpdateClientUserMutation.isPending}
              >
                {addUpdateClientUserMutation.isPending
                  ? "Saving..."
                  : editMode
                  ? "Update Client User"
                  : "Add Client User"}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteConfirmation.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Confirm Delete</h3>
            </div>
            {deleteUserMutation.isError && (
              <div className="error-message-modal">
                {deleteUserMutation.error.response?.data?.message ||
                  deleteUserMutation.error.message ||
                  "An error occurred."}
              </div>
            )}
            <div className="modal-body">
              <p>
                Are you sure you want to delete user{" "}
                <strong>{deleteConfirmation.userName}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() =>
                  setDeleteConfirmation({
                    isOpen: false,
                    userId: null,
                    userName: "",
                    userType: "",
                  })
                }
                className="cancel-btn"
                disabled={deleteUserMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="delete-confirm-btn"
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
 